import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CapturedValues } from "@/components/BondRecord";
import { ChangeMarker } from "@/components/BondDetailsFields";
import { CollapsibleBox } from "@/components/CollapsibleBox";
import { buttonClass } from "@/components/buttonStyles";
import { LockedButton } from "@/components/bond-submission/SubmissionShell";
import type { AddressParts } from "@/lib/address";
import type { BondRenderField } from "@/lib/bondFields";
import {
  bondProfileSections,
  projectAddressField,
} from "@/lib/bondSubmissionProfile";
import { bondFieldMarkers, readChangeRequest } from "@/lib/bondStateModel";
import type { BondFlow } from "@/lib/brokerBonds";
import { googleMapsSearchUrl } from "@/lib/mapLink";
import type { SubmissionActionState } from "@/lib/submissionShell";

// The Project Details box (BRK-S140's record, BRK-S415's markers, BRK-S502's
// box): ONE collapsible "Project Details" box with Edit Bond Request at its
// right, holding the configuration's final-step fields grouped by
// `bondProfileSections` into Obligee / Project / Bond terms / Additional
// details, every value formatted from its declared type (D-10) by
// `CapturedValues`, and "View on map" on the project address. The same box
// sits on the Submission Profile and the Project Details tab.
//
// A PENDING CHANGE (`changeRequest`, PMS-S837 — an edit before award or a
// rider after) marks each field it names: the field shows the REQUESTED value,
// with "Pending approval" and "Currently on bond: {value}" under it — the
// marker copy `BondDetailsFields` draws in the edit dialogs, reused rather
// than restated. Only fields PMS names are marked; nothing is diffed here.
// Both values are captured-value representations (never formatted strings),
// so each is formatted by the field's own type like every other value.
//
// AN APPROVED CHANGE (`recentChanges`, PMS-S857 — BRK-S420) marks
// each field it names "Updated"; a field still pending shows only its pending
// marker (`bondFieldMarkers`, the same reading the rider dialog's markers
// take). Served, never diffed.

export function ProjectDetailsSections({
  fields,
  values,
  changeRequest,
  recentChanges,
}: {
  /** The final-step fields, through the shared adapter (`toRenderFinalField`). */
  fields: readonly BondRenderField[];
  /** The captured bag (`values.final`). */
  values: Readonly<Record<string, unknown>>;
  /** The order view's `changeRequest`, read tolerantly. */
  changeRequest: unknown;
  /** The order view's `recentChanges`, read tolerantly (BRK-S420). */
  recentChanges?: unknown;
}) {
  const pending = readChangeRequest(changeRequest)?.fields ?? {};
  const { markers, onBond } = bondFieldMarkers({
    changeRequest,
    recentChanges,
  });
  const shown: Record<string, unknown> = { ...values };
  for (const [key, change] of Object.entries(pending)) {
    shown[key] = change.requested;
  }
  const sections = bondProfileSections(fields, shown);
  const projectAddress = projectAddressField(fields);
  const mapUrl = projectAddress
    ? googleMapsSearchUrl(addressOf(shown[projectAddress.name]))
    : null;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      {sections.map((section) => (
        <ProjectSection key={section.id} title={section.title}>
          <CapturedValues
            fields={section.fields}
            values={shown}
            emptyNote="Nothing captured in this section."
            extra={(field) => (
              <>
                {/* The project address gains a map link, and only that
                      field. */}
                {field.name === projectAddress?.name && mapUrl !== null && (
                  <>
                    {" "}
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap text-sm font-normal text-brk-accent hover:underline"
                    >
                      View on map
                    </a>
                  </>
                )}
                {Object.hasOwn(markers, field.name) && (
                  <ChangeMarker
                    marker={markers[field.name]}
                    field={field}
                    onBond={onBond[field.name]}
                  />
                )}
              </>
            )}
          />
        </ProjectSection>
      ))}
    </div>
  );
}

function ProjectSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex min-w-0 flex-col gap-3">
      <h3
        id={id}
        className="text-xs font-semibold uppercase tracking-wide text-brk-dim"
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The "Project Details" box: the sections above, or an honest line when the
 * configuration could not be read, a standalone pre-approval captured no
 * terms, or nothing was captured yet — never an old panel. Edit Bond Request
 * at its right is the action row's own read of `actions.edit`.
 */
export function ProjectDetailsBox({
  fields,
  values,
  changeRequest,
  recentChanges,
  flow,
  accountId,
  edit,
  onEdit,
}: {
  /** The adapted final-step fields; null when the configuration read failed. */
  fields: readonly BondRenderField[] | null;
  values: Readonly<Record<string, unknown>>;
  changeRequest: unknown;
  recentChanges?: unknown;
  flow: BondFlow;
  accountId: string;
  edit: SubmissionActionState | undefined;
  onEdit: () => void;
}) {
  const button = buttonClass({ variant: "secondary", size: "sm" });
  const actions =
    edit === undefined ? undefined : edit.enabled ? (
      <button type="button" onClick={onEdit} className={button}>
        {edit.label}
      </button>
    ) : (
      <LockedButton
        label={edit.label}
        reason={edit.reason}
        className={button}
      />
    );
  const sections =
    fields === null ? [] : bondProfileSections(fields, { ...values });
  let body: ReactNode;
  if (fields === null) {
    body = (
      <p className="text-sm text-brk-dim">
        We couldn’t load this bond’s field labels, so the submitted terms aren’t
        shown here. The request itself is unaffected.
      </p>
    );
  } else if (flow === "preapproval" && sections.length === 0) {
    // A standalone pre-approval captured no terms: say WHERE the answer
    // lives (BRK-S160).
    body = (
      <p className="text-sm text-brk-dim">
        This request was a program pre-approval; the decision lives on the
        account’s Surety program.{" "}
        <Link
          to={`/surety/accounts/${accountId}`}
          className="text-brk-accent hover:underline"
        >
          View the account
        </Link>
      </p>
    );
  } else if (sections.length === 0) {
    body = (
      <p className="text-sm text-brk-dim">
        Nothing was captured for this request’s project and bond terms yet.
      </p>
    );
  } else {
    body = (
      <ProjectDetailsSections
        fields={fields}
        values={values}
        changeRequest={changeRequest}
        recentChanges={recentChanges}
      />
    );
  }
  return (
    <CollapsibleBox title="Project Details" actions={actions}>
      {body}
    </CollapsibleBox>
  );
}

/** A captured address value as map parts, narrowed from the bag; anything
 *  that is not an address object reads as none (and the link is dropped). */
function addressOf(raw: unknown): AddressParts | null {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const part = (key: string): string => {
    const v: unknown = Reflect.get(raw, key);
    return typeof v === "string" ? v : "";
  };
  return {
    street: part("street"),
    addressLine2: part("addressLine2") || null,
    city: part("city"),
    state: part("state"),
    zip: part("zip"),
  };
}
